import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Collapse,
  Divider,
  Grid,
  Table,
  TableRow,
  TableCell,
  Typography,
  TextField,
  IconButton,
  Box,
} from "@mui/material";
import { Playback } from "../../../pages/api/replay/list";
import { DateTime, Interval } from "luxon";
import useSWR, { mutate } from "swr";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { set, update } from "../../redux/slices/replay";
import axios from "axios";
import { Delete } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { clear } from "../Map/Marker/trackerSlice";
import { clear as clearWindows } from "../../trackerWindowsSlice";
import { ReplayInfo } from "./ReplayControls";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  getFormattedDateTime,
  getHourCycle,
  getFriendlyTimezoneName,
  getDateOrderForTimezone,
  getDateFormatString,
} from "../../utils/dateTimeUtils";

//@ts-ignore
const fetcher = (...args) => fetch(...args).then((res) => res.json());

export default function ReplayListWrapper(props: {
  openControls: Function;
  defaultReplays: { replays: FetchedPlayback[] };
  timezone: string;
}) {
  // 1) Read whether we are saving from Redux
  const saving = useAppSelector((state) => state.replay.saving);

  const { data } = useSWR<{ replays: FetchedPlayback[] }>(
    "/api/replay/list",
    fetcher,
    {
      fallbackData: props.defaultReplays,
      // 2) If "saving" is true, turn off polling
      refreshInterval: saving ? 0 : 10000,
    }
  );
  const replays = (data?.replays ?? [])
    .map((replay) => parseFetchedPlayback(replay))
    .sort((a, b) => a.createdAt.toUnixInteger() - b.createdAt.toUnixInteger());

  return (
    <Box
      id="replay-list-scroll-container"
      sx={{ maxHeight: "60vh", overflowY: "scroll" }}
    >
      <ReplayList
        replays={replays}
        openControls={() => props.openControls()}
        timezone={props.timezone}
      />
    </Box>
  );
}

export function ReplayList(props: ReplayListProps) {
  const [selected, setSelected] = useState(-1);
  const [cachedExpandedInfo, setCachedExpandedInfo] = useState<{
    [key: string]: ReplayInfo;
  }>();
  const [loadingInfo, setLoadingInfo] = useState<Set<string>>(new Set());

  const fetchInfo = (replay: Playback) => {
    setLoadingInfo((prev) => new Set(prev).add(replay.name));
    axios
      .get("/api/replay/info", {
        params: { start: replay.start.toISO(), end: replay.end.toISO() },
      })
      .then((res) =>
        setCachedExpandedInfo((prevState) => ({
          ...prevState,
          [replay.name]: res.data,
        }))
      )
      .finally(() => {
        setLoadingInfo((prev) => {
          const next = new Set(prev);
          next.delete(replay.name);
          return next;
        });
      });
  };

  // Function to reset selection state
  const resetSelection = useCallback(() => {
    setSelected(-1);
  }, []);

  return (
    <Grid
      container
      justifyContent={"start"}
      alignItems={"center"}
      flexDirection={"column"}
    >
      {props.replays.map((replay, i) => (
        <Box
          key={"replay-" + i}
          sx={{ marginY: 0.25, marginRight: 0.25, width: "100%" }}
        >
          <ReplayItem
            openControls={props.openControls}
            replay={replay}
            selected={i === selected}
            onClick={() =>
              setSelected((prevState) => (prevState === i ? -1 : i))
            }
            resetSelection={resetSelection}
            fetchInfo={fetchInfo}
            info={cachedExpandedInfo?.[replay.name]}
            isLoadingInfo={loadingInfo.has(replay.name)}
            timezone={props.timezone}
          />
          {i !== props.replays.length - 1 && <Divider variant={"middle"} />}
        </Box>
      ))}
    </Grid>
  );
}

function ReplayItem(props: {
  replay: Playback;
  selected: boolean;
  onClick: Function;
  openControls: Function;
  fetchInfo: (replay: Playback) => any;
  info?: ReplayInfo;
  isLoadingInfo: boolean;
  timezone: string;
  resetSelection: Function;
}) {
  const { replay } = props;
  const interval = Interval.fromDateTimes(replay.start, replay.end);
  const duration = interval.toDuration().toFormat("mm") + " mins";
  const replayTz = replay.timezone || props.timezone;

  // Get the date order and format string
  const dateOrder = getDateOrderForTimezone(replayTz);
  const dateFormat = getDateFormatString(dateOrder);

  // Get the date (displayed as short date only, but now with correct order)
  const date = replay.start.setZone(replayTz).toFormat(dateFormat);

  // Get a friendly version of the timezone name
  const timezoneFriendly = getFriendlyTimezoneName(replayTz);

  // Get the start and end times (converted properly)
  const fromTime = replay.start
    .setZone(replayTz)
    .toLocaleString({ timeStyle: "short", hourCycle: getHourCycle(replayTz) });
  const toTime = replay.end
    .setZone(replayTz)
    .toLocaleString({ timeStyle: "short", hourCycle: getHourCycle(replayTz) });

  const dispatch = useAppDispatch();
  const loadReplay = useCallback(() => {
    // Cast start and end to numbers if they are strings
    const startMillis =
      typeof replay.start === "object"
        ? replay.start.toMillis()
        : typeof replay.start === "string"
          ? DateTime.fromISO(replay.start).toMillis()
          : replay.start;

    const endMillis =
      typeof replay.end === "object"
        ? replay.end.toMillis()
        : typeof replay.end === "string"
          ? DateTime.fromISO(replay.end).toMillis()
          : replay.end;

    dispatch(
      set({
        running: false,
        startDate: startMillis,
        endDate: endMillis,
        isReplay: true,
        lastTimestamp: startMillis,
        name: replay.name,
        started: Date.now(),
        controlsOpen: true,
        firstLoad: true,
        isFromRemote: false,
        saving: false,
        currentIMEIs: [],
        loading: true,
        message: "Loading replay...",
        timezone: replay.timezone,
        ready: false,
        error: null,
        forceStop: false, // Reset the force stop flag when loading a new replay
      })
    );
    dispatch(clearWindows());
    dispatch(clear());
    axios
      .post("/api/replay/sendCommand", {
        command: "load",
        name: replay.name,
        start: startMillis,
        end: endMillis,
        timezone: replay.timezone,
      })
      .catch((error) => {
        console.error("Error sending load command:", error);
        dispatch(
          update({ loading: false, error: "Failed to start loading replay" })
        );
      });
    props.openControls();
  }, [dispatch, replay, props.openControls]);
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(props.replay.notes);

  const saveNotes = useCallback(() => {
    updateReplayNotes(props.replay.name, notes)
      .then((res) => {
        setEditing(false);
        if (!!res.data?.replays) {
          mutate("/api/replay/list", res.data).catch(console.error);
        }
      })
      .catch(console.error);
  }, [notes]);

  const deleteReplay = () => {
    const shouldDelete = confirm(`Delete \"${props.replay.name}\"?`);
    if (shouldDelete) {
      axios
        .post("/api/replay/delete", replay)
        .then((res) => {
          props.resetSelection();
          mutate("/api/replay/list", res.data).catch(console.error);
        })
        .catch(console.error);
    }
  };

  // const {data: replayInfo} = useSWR<ReplayInfo>(`/api/replay/info?start=${props.replay.start.toISO()}&end=${props.replay.end.toISO()}`, fetcher)

  const permissions = useAppSelector((state) => state.permissions);

  const [showCounts, setShowCounts] = useState(false);
  const loadExpandedInfo = useCallback(() => {
    if (!props.isLoadingInfo && !props.info) {
      props.fetchInfo(props.replay);
    }
    setShowCounts((old) => !old);
  }, [showCounts, props.replay, props.info, props.isLoadingInfo]);

  useEffect(() => {
    if (!props.selected) {
      setShowCounts(false);
      // Also exit edit mode when deselected
      setEditing(false);
    }
  }, [props.selected]);

  const itemRef = useRef<HTMLDivElement>(null);

  // Scroll effect for selection OR showing counts
  useEffect(() => {
    // Check visibility *after* collapse animation/render
    const timer = setTimeout(() => {
      const element = itemRef.current;
      if (!element) return;

      const scrollContainer = document.getElementById(
        "replay-list-scroll-container"
      );
      if (!scrollContainer) return; // Exit if scroll container not found

      const elementRect = element.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      const shouldScroll =
        (props.selected || (showCounts && props.info)) && // Must be selected OR showing counts (with info)
        elementRect.bottom > containerRect.bottom; // AND element bottom is below container bottom

      if (shouldScroll) {
        element.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }, 200); // Unified timeout, slightly longer to accommodate potential fetch/render

    return () => clearTimeout(timer); // Cleanup timeout
  }, [props.selected, showCounts, props.info]); // Rerun when selection, counts visibility, or info changes

  return (
    <>
      <Card sx={{ width: "100%" }} ref={itemRef}>
        <CardContent>
          <Grid container justifyContent={"space-between"} alignItems={"start"}>
            <Grid item flexGrow={1}>
              <CardActionArea onClick={() => props.onClick()}>
                <Typography variant={"body1"}>{replay.name}</Typography>
              </CardActionArea>
            </Grid>
            <Grid item sx={{ display: props.selected ? "" : "none" }}>
              <IconButton
                onClick={() => (editing ? deleteReplay() : props.onClick())}
                sx={{ padding: 0, marginTop: -0.25 }}
              >
                {editing ? (
                  <Delete color={"error"} />
                ) : (
                  // Restore opacity to hide CloseIcon when not editing
                  <CloseIcon sx={{ opacity: 0 }} color={"error"} />
                )}
              </IconButton>
            </Grid>
          </Grid>

          <Typography variant={"caption"}>
            Date: {date} | Duration: {duration} | Timezone: {timezoneFriendly}
            <br />
            From {fromTime} to {toTime}
          </Typography>

          <Collapse in={props.selected} sx={{ marginTop: 1 }}>
            {editing ? (
              <Grid container justifyContent={"space-between"} sx={{ mb: 1 }}>
                <Button
                  variant={"outlined"}
                  onClick={() => saveNotes()}
                  color={"success"}
                  size="small"
                >
                  Save
                </Button>
                <Button
                  variant={"outlined"}
                  onClick={() => setEditing(false)}
                  color={"error"}
                  size="small"
                >
                  Cancel
                </Button>
              </Grid>
            ) : (
              <Grid container justifyContent={"space-between"} sx={{ mb: 1 }}>
                <Button
                  variant={"outlined"}
                  onClick={() => loadReplay()}
                  size="small"
                >
                  Load
                </Button>
                <Button
                  variant={"outlined"}
                  onClick={() => setEditing(true)}
                  color={"primary"}
                  size="small"
                >
                  Edit
                </Button>
              </Grid>
            )}
          </Collapse>

          <Collapse in={props.selected}>
            <Table size={"small"} sx={{ marginTop: 1, marginBottom: 1 }}>
              <TableRow>
                <TableCell>
                  <Typography variant={"body2"}>Created At</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant={"body2"}>
                    {getFormattedDateTime(replay.createdAt, replayTz, false)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography variant={"body2"}>Created By</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant={"body2"}>{replay.createdBy}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography variant={"body2"}>Start</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant={"body2"}>
                    {getFormattedDateTime(replay.start, replayTz, false)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography variant={"body2"}>End</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant={"body2"}>
                    {getFormattedDateTime(replay.end, replayTz, false)}
                  </Typography>
                </TableCell>
              </TableRow>
              {permissions.textid === "sysAdmin" && (
                <TableRow>
                  <TableCell>
                    <Typography variant={"body2"}>ID</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant={"body2"}>
                      {replay.directory}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {
                <TableRow
                  sx={{
                    cursor: "pointer",
                    borderBottom: showCounts ? "2px black solid" : "inherit",
                  }}
                  onClick={() => loadExpandedInfo()}
                >
                  <TableCell sx={{ color: "primary" }}>
                    <LoadingButton
                      size={"small"}
                      loading={props.isLoadingInfo && !props.info}
                    >
                      {showCounts ? "Hide Counts" : "Show Counts"}
                    </LoadingButton>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              }
              {showCounts &&
                props.info?.counts &&
                Object.entries(props.info?.counts).map((entry) => (
                  <TableRow key={entry[0]}>
                    <TableCell>{`${entry[0]}`}</TableCell>
                    <TableCell>{`${entry[1]}`}</TableCell>
                  </TableRow>
                ))}
            </Table>
            <Typography variant={"body2"} fontWeight={"bold"}>
              Notes:
            </Typography>
            {editing ? (
              <TextField
                sx={{ marginBottom: 0.5 }}
                name={"Notes"}
                size={"small"}
                multiline
                maxRows={3}
                minRows={1}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            ) : (
              <Typography variant={"body2"} sx={{ maxWidth: "13vw" }} paragraph>
                {replay.notes}
              </Typography>
            )}
          </Collapse>
        </CardContent>
      </Card>
    </>
  );
}

// Rest of the file restored...
interface ReplayListProps {
  replays: Playback[];
  openControls: Function;
  timezone: string;
}

async function updateReplayNotes(name: string, notes: string) {
  return await axios.post("/api/replay/updateNotes", {
    name,
    notes,
  });
}

function parseFetchedPlayback(playback: FetchedPlayback): Playback {
  const createdAt =
    typeof playback.createdAt === "number"
      ? DateTime.fromMillis(playback.createdAt)
      : DateTime.fromISO(playback.createdAt);
  const start =
    typeof playback.start === "number"
      ? DateTime.fromMillis(playback.start)
      : DateTime.fromISO(playback.start);
  const end =
    typeof playback.end === "number"
      ? DateTime.fromMillis(playback.end)
      : DateTime.fromISO(playback.end);
  return {
    ...playback,
    createdAt: createdAt,
    start: start,
    end: end,
  };
}

export interface FetchedPlayback {
  name: string;
  start: string | number;
  end: string | number;
  createdAt: string | number;
  createdBy: string;
  notes: string;
  timezone: string;
  command?: string; // Optional command property
}
